import React, { useEffect, useMemo, useState } from "react";
import styles from "./task-list-table.module.css";
import { Task } from "../../types/public-types";
import { useGetUsersQuery } from "../../../../../services/usersApi";
import { useGetInstitutionsQuery } from "../../../../../services/institutionsApi";
import { renderAvatarRow } from "../../../../../utils/avatar";
import { useChannelQuery } from "../../../../../services/websocketApi";
import { BranchView } from '../../../../../components/BranchDropdown';

// Changes:
// Removed "weekday" and from dateTimeOptions
// Changed "month" to "numeric" in dateTimeOptions
// Added column for user
// The component now fetches a list of users using Redux
// The component displays the avatar of the user assigned to each task
// In case of a group, the component displays a row of avatars of the users assigned to each task in the group
// Modified table css to center the expander symbol


const localeDateStringCache: Record<string, string> = {};
const toLocaleDateStringFactory =
  (locale: string) =>
    (date: Date, dateTimeOptions: Intl.DateTimeFormatOptions) => {
      const key = date.toString();
      let lds = localeDateStringCache[key];
      if (!lds) {
        lds = date.toLocaleDateString(locale, dateTimeOptions);
        localeDateStringCache[key] = lds;
      }
      return lds;
    };
const dateTimeOptions: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "numeric",
  day: "2-digit",
};

export const TaskListTableDefault: React.FC<{
  nameColumnWidth: number;
  rowHeight: number;
  rowWidth: string;
  fontFamily: string;
  fontSize: string;
  locale: string;
  tasks: Task[];
  selectedTaskId: string;
  setSelectedTask: (taskId: string) => void;
  onExpanderClick: (task: Task) => void;
  onTaskReorder?: (draggedTask: Task, targetTask: Task) => void;
  columnVisibility?: {
    coordinator?: boolean;
    executor?: boolean;
    branch?: boolean;
    name?: boolean;
    start?: boolean;
    end?: boolean;
    progress?: boolean;
  };
  columnOrder?: string[];
  columnWidths?: Record<string, number>;
  collapsedChains?: Set<string>;
  onChainToggle?: (chainParentId: string) => void;
}> = ({
  nameColumnWidth,
  rowHeight,
  rowWidth,
  tasks = [],
  fontFamily,
  fontSize,
  locale,
  onExpanderClick,
  onTaskReorder,
  columnVisibility,
  columnOrder,
  columnWidths,
  collapsedChains,
  onChainToggle,
}) => {
    const visibility = columnVisibility || {
      coordinator: true,
      executor: true,
      branch: true,
      name: true,
      start: true,
      end: true,
      progress: true
    };

    const defaultOrder = ['coordinator', 'executor', 'branch', 'name', 'start', 'end', 'progress'];
    const order = columnOrder || defaultOrder;

    const defaultWidths: Record<string, number> = {
      coordinator: parseFloat(rowWidth),
      executor: parseFloat(rowWidth),
      branch: parseFloat(rowWidth),
      name: nameColumnWidth,
      start: parseFloat(rowWidth),
      end: parseFloat(rowWidth),
      progress: parseFloat(rowWidth),
    };

    const widths = columnWidths || defaultWidths;
    
    const toLocaleDateString = useMemo(
      () => toLocaleDateStringFactory(locale),
      [locale]
    );

    const [draggedTask, setDraggedTask] = useState<Task | null>(null);
    const [dragOverTask, setDragOverTask] = useState<Task | null>(null);

    const { data: usersResult, isLoading: usersLoading, error: usersError, refetch: usersRefetch } = useGetUsersQuery("");
    const { data: institutionsResult, isLoading: institutionsLoading, error: institutionsError, refetch: institutionsRefetch } = useGetInstitutionsQuery("");

    const { data: userEvents } = useChannelQuery('users');

    useEffect(() => {
      if (userEvents?.messages.length && usersResult) {
        usersRefetch();
      }
    }, [userEvents]);

    const { data: institutionEvents } = useChannelQuery('institutions');

    useEffect(() => {
      if (institutionEvents?.messages.length && institutionsResult) {
        institutionsRefetch();
      }
    }, [institutionEvents]);

    const handleDragStart = (e: React.DragEvent, task: Task) => {
      setDraggedTask(task);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/html", e.currentTarget.innerHTML);
    };

    const handleDragOver = (e: React.DragEvent, task: Task) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      
      if (!draggedTask || draggedTask.id === task.id) {
        return;
      }

      // Only allow reordering within same context (same project for tasks, or both are projects)
      if (draggedTask.type === 'project' && task.type === 'project') {
        setDragOverTask(task);
      } else if (draggedTask.type === 'task' && task.type === 'task' && draggedTask.project === task.project) {
        setDragOverTask(task);
      }
    };

    const handleDragLeave = () => {
      setDragOverTask(null);
    };

    const handleDrop = (e: React.DragEvent, targetTask: Task) => {
      e.preventDefault();
      
      if (draggedTask && onTaskReorder && draggedTask.id !== targetTask.id) {
        // Check if reordering is allowed
        if (draggedTask.type === 'project' && targetTask.type === 'project') {
          onTaskReorder(draggedTask, targetTask);
        } else if (draggedTask.type === 'task' && targetTask.type === 'task' && draggedTask.project === targetTask.project) {
          onTaskReorder(draggedTask, targetTask);
        }
      }
      
      setDraggedTask(null);
      setDragOverTask(null);
    };

    const handleDragEnd = () => {
      setDraggedTask(null);
      setDragOverTask(null);
    };



    if (usersLoading || institutionsLoading) return <div>Loading...</div>;
    if (usersError || institutionsError) return <div>Error loading data</div>;

    const users = [...(usersResult || []), ...(institutionsResult || [])];
    const hasProjectRows = tasks.some(task => task.type === "project");

    const renderCell = (columnId: string, t: Task, expanderSymbol: string, onExpanderClickHandler: () => void) => {
      if (!visibility[columnId as keyof typeof visibility]) {
        return null;
      }

      const width = widths[columnId] || (columnId === 'name' ? nameColumnWidth : parseFloat(rowWidth));

      switch (columnId) {
        case 'coordinator':
          return (
            <div
              key={`${t.id}-coordinator`}
              className={styles.taskListCell}
              style={{
                minWidth: width,
                maxWidth: width,
              }}
            >
              <div className={styles.taskListNameWrapper}>
                <div
                  className={
                    expanderSymbol
                      ? styles.taskListExpander
                      : styles.taskListEmptyExpander
                  }
                  onClick={onExpanderClickHandler}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    minWidth: "20px",
                  }}
                >
                  {expanderSymbol}
                </div>
                <div>
                  {(() => {
                    if (t.type === "project" && t.coordinators && t.coordinators.length > 0) {
                      const foundCoordinators = users?.filter((user: { _id: string }) =>
                        t.coordinators?.includes(user._id)
                      );
                      
                      const sortedCoordinators = foundCoordinators?.sort((a, b) => {
                        const branchA = a.branch || '';
                        const branchB = b.branch || '';
                        return branchA.localeCompare(branchB);
                      });
                      
                      return sortedCoordinators && sortedCoordinators.length > 0
                        ? renderAvatarRow(sortedCoordinators)
                        : null;
                    }
                    return null;
                  })()}
                </div>
              </div>
            </div>
          );

        case 'executor':
          return (
            <div
              key={`${t.id}-executor`}
              className={styles.taskListCell}
              style={{
                minWidth: width,
                maxWidth: width,
              }}
              title={t.name}
            >
              {(() => {
                if (t.type === "task") {
                  const foundUsers = users?.filter((user: { _id: string }) =>
                    t.users?.includes(user._id)
                  );

                  const sortedUsers = foundUsers?.sort((a, b) => {
                    const branchA = a.branch || '';
                    const branchB = b.branch || '';
                    return branchA.localeCompare(branchB);
                  });

                  return sortedUsers?.length ? renderAvatarRow(sortedUsers) : null;
                }

                if (t.type === "project") {
                  const projectUserIds = !t.hideChildren
                    ? tasks.filter(task => task.project === t.id).flatMap(task => task.users ?? [])
                    : t.hiddenChildren?.flatMap(task => task.users ?? []) ?? [];

                  const uniqueIds = [...new Set(projectUserIds)];

                  if (uniqueIds.length > 0) {
                    const foundUsers = users?.filter((user: { _id: string }) =>
                      uniqueIds.includes(user._id)
                    );

                    const sortedUsers = foundUsers?.sort((a, b) => {
                      const branchA = a.branch || '';
                      const branchB = b.branch || '';
                      return branchA.localeCompare(branchB);
                    });

                    return sortedUsers && sortedUsers.length > 0
                      ? renderAvatarRow(sortedUsers)
                      : null;
                  }
                }

                return null;
              })()}
            </div>
          );

        case 'branch':
          return (
            <div
              key={`${t.id}-branch`}
              className={styles.taskListCell}
              style={{
                minWidth: width,
                maxWidth: width
              }}>
              {(() => {
                const renderBranches = (usersList: any[]) => {
                  if (!usersList || usersList.length === 0) return null;

                  const rawBranches = usersList.map(u => u?.branch);

                  const validBranches = rawBranches.filter(
                    (b): b is string => Boolean(b && typeof b === "string")
                  );

                  const uniqueBranches = Array.from(new Set(validBranches));

                  if (uniqueBranches.length === 0) {
                    return (
                      <BranchView
                        value={""}
                      />
                    );
                  }

                  if (uniqueBranches.length !== 1) {
                    return null;
                  }

                  const branch = uniqueBranches[0];

                  return (
                    <BranchView
                      value={branch}
                      short={true}
                    />
                  );
                };

                if (t.type === "task") {
                  const foundUsers = users?.filter((user) =>
                    t.users?.includes(user._id)
                  );

                  return foundUsers?.length ? renderBranches(foundUsers) : null;
                }

                if (t.type === "project") {
                  const projectUserIds = !t.hideChildren
                    ? tasks
                      .filter((task) => task.project === t.id)
                      .flatMap((task) => task.users ?? [])
                    : t.hiddenChildren?.flatMap((task) => task.users ?? []) ?? [];

                  const uniqueIds = Array.from(new Set(projectUserIds));

                  if (uniqueIds.length > 0) {
                    const foundUsers = users?.filter((user) =>
                      uniqueIds.includes(user._id)
                    );

                    return foundUsers?.length ? renderBranches(foundUsers) : null;
                  }
                }

                return null;
              })()}
            </div>
          );

        case 'name':
          return (
            <div
              key={`${t.id}-name`}
              className={styles.taskListCell}
              style={{
                minWidth: width,
                maxWidth: width,
                fontStyle: t.name ? "normal" : "italic",
                paddingLeft: t.type === "task" && hasProjectRows ? "20px" : "0"
              }}
            >
              {t.name || '(Bez nazwy)'}
            </div>
          );

        case 'start':
          return (
            <div
              key={`${t.id}-start`}
              className={styles.taskListCell}
              style={{
                minWidth: width,
                maxWidth: width,
              }}
            >
              &nbsp;{toLocaleDateString(t.start, dateTimeOptions)}
            </div>
          );

        case 'end':
          return (
            <div
              key={`${t.id}-end`}
              className={styles.taskListCell}
              style={{
                minWidth: width,
                maxWidth: width,
              }}
            >
              &nbsp;{toLocaleDateString(t.end, dateTimeOptions)}
            </div>
          );

        case 'progress':
          return (
            <div
              key={`${t.id}-progress`}
              className={styles.taskListCell}
              style={{
                minWidth: width,
                maxWidth: width,
              }}
            >
              &nbsp;{t.progress !== undefined && t.progress > 0 ? `${t.progress}%` : ''}
            </div>
          );

        default:
          return null;
      }
    };


    return (



      <div
        className={styles.taskListWrapper}
        style={{
          fontFamily: fontFamily,
          fontSize: fontSize,
        }}
      >
        {tasks.map(t => {
          let expanderSymbol = "";
          let onExpanderClickHandler = () => onExpanderClick(t);
          
          // Check for chain parent collapse/expand
          if (t.isChainParent && onChainToggle) {
            const isCollapsed = collapsedChains?.has(t.id);
            expanderSymbol = isCollapsed ? "►" : "▼";
            onExpanderClickHandler = () => onChainToggle(t.id);
          }
          // Check for project collapse/expand
          else if (t.hideChildren === false) {
            expanderSymbol = "▼";
          } else if (t.hideChildren === true) {
            expanderSymbol = "▶";
          }

          return (
            <div
              className={`${styles.taskListTableRow} ${draggedTask?.id === t.id ? styles.dragging : ''} ${dragOverTask?.id === t.id ? styles.dropTarget : ''}`}
              style={{ 
                height: rowHeight,
                backgroundColor: t.type === "project" ? "#e0e0e0" : undefined
              }}
              key={`${t.id}row`}
              draggable={true}
              onDragStart={(e) => handleDragStart(e, t)}
              onDragOver={(e) => handleDragOver(e, t)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, t)}
              onDragEnd={handleDragEnd}
            >
              {order.map((columnId) => renderCell(columnId, t, expanderSymbol, onExpanderClickHandler))}
            </div>
          );
        })}
      </div>
    );
  };
